import { useCommonUserRetrieve } from "@boundless/bl-api-client/common/services"
import { EstimatedRevenueEnumLabels } from "@boundless/bl-api-client/customer/models/EstimatedRevenueEnumLabels"
import { IndustryEnumLabels } from "@boundless/bl-api-client/customer/models/IndustryEnumLabels"
import { InfoIcon } from "lucide-react"

import { OnboardingFooter } from "@/components/onboarding/OnboardingFooter"
import { OnboardingHero } from "@/components/onboarding/OnboardingHero"
import type { BusinessAnswers } from "@/components/onboarding/steps/BusinessStep"
import type { FundingAnswers } from "@/components/onboarding/steps/FundingStep"
import { DialogTitle } from "@/components/ui/dialog"

interface Props {
  /** Null when the save failed, rendered as an em dash. */
  matchCount: number | null
  business: BusinessAnswers
  funding: FundingAnswers
  onNext: () => void
}

/**
 * The reveal: the number, the answers that produced it, then the caveat.
 * Showing the inputs is what makes the number read as computed rather than
 * decorative.
 *
 * Only answers the count actually filters on earn a chip. Use of funds is not
 * one of them — `LenderProfileQuerySet.prequalifying` deliberately ignores it
 * — so echoing it here would credit the number to a question it never asked.
 */
export function MatchStep({ matchCount, business, funding, onNext }: Props) {
  // A partner's borrower must not see Boundless named in their own reveal, so
  // the neutral wording is also what shows while the user is still loading.
  const { data: user } = useCommonUserRetrieve()
  const network =
    user && !user.partnerSlug ? "the Boundless network" : "our network"

  const chips = [
    {
      label: "Industry",
      value: IndustryEnumLabels[business.industry] ?? "—",
    },
    {
      label: "Revenue",
      value: EstimatedRevenueEnumLabels[business.estimatedRevenue] ?? "—",
    },
    {
      label: "Seeking",
      value: `$${funding.capitalAmount.toLocaleString()}`,
    },
  ]

  return (
    <>
      <OnboardingHero badge="Initial assessment" size="reveal">
        <div className="relative z-20 text-center">
          {/* The number carries the reveal on screen, but on its own it is a
              nameless dialog to a screen reader, announced as a bare digit. */}
          <DialogTitle className="sr-only">
            Your preliminary lender match
          </DialogTitle>
          <p
            className="text-[70px] leading-[0.94] font-bold tracking-[-0.045em] text-white tabular-nums"
            data-testid="onboarding-match-count"
          >
            {matchCount ?? "—"}
          </p>
          <p className="mt-2 text-base font-semibold text-white">
            potential lender matches
          </p>
          <p className="mt-1 text-[12.5px] text-white/70">
            across 180+ lenders in {network}
          </p>
        </div>
      </OnboardingHero>

      <div className="px-9 pt-8">
        <div className="mb-4 grid grid-cols-3 gap-2">
          {chips.map((chip) => (
            <div
              key={chip.label}
              className="flex min-w-0 flex-col gap-[3px] rounded-[11px] border border-slate-100 bg-slate-50 px-3 py-[11px]"
            >
              <span className="text-[10.5px] font-semibold tracking-[0.06em] text-slate-400 uppercase">
                {chip.label}
              </span>
              <strong className="truncate text-[13px] font-semibold tracking-[-0.01em]">
                {chip.value}
              </strong>
            </div>
          ))}
        </div>

        <div className="flex items-start gap-[9px]">
          <InfoIcon className="mt-[1.5px] size-[15px] shrink-0 text-slate-400" />
          <p className="text-muted-foreground text-[13px] leading-[1.55]">
            A quick estimate, not a guarantee.{" "}
            <b className="text-foreground font-semibold">Finish your profile</b>{" "}
            and we&rsquo;ll confirm which of these lenders will actually make
            you one.
          </p>
        </div>
      </div>

      <OnboardingFooter label="Fill out your form" onNext={onNext} />
      <div className="pb-[34px]" />
    </>
  )
}
